// The Wi-Fi networks a device HOLDS in its chip — read-only, live over MQTT.
//
// Publishes `getallwifi` and waits for the answer on `<uno>/client`; parseWifiList
// owns the three wire formats (ESP8266 CSV, ESP32 CSV + JSON, `EMPTY`). A read
// never changes anything on the device — unlike ADDWIFI / REMOVEWIFI, which
// reboot it (those stay in DeviceWifiPanel / WifiRemoveDialog).
//
// ⚠️ THE REPLY CARRIES PASSWORDS. The hook returns them; the CALLER decides
// whether to show them (deviceWifi.canSeeWifiPassword — Super Admin or owner).

import { useCallback, useEffect, useRef, useState } from 'react';
import { publishCommand } from '../utils/deviceController';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';
import { CMD_GET_ALL_WIFI, parseWifiList } from '../devices/commands';

const LIST_TIMEOUT_MS = 6000;
// `EMPTY` is a real answer ("I hold nothing"); a status frame also parses to [].
const SAYS_EMPTY = (payload) => /^(\[[\w-]{1,32}\]\s*)?EMPTY$/i.test(String(payload).trim());

/**
 * @param uno      the device's unique number (falsy = do nothing)
 * @param userId   the sender id for `#<userId>`
 * @param enabled  read only while true (e.g. while the dialog is open)
 * @returns { list: [{ssid, pass}] | null, loading, silent, refresh }
 *          list null = not answered yet; silent = timed out (offline / asleep)
 */
export default function useDeviceWifiList(uno, userId, enabled = true) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [silent, setSilent] = useState(false);
  const awaitingRef = useRef(false);
  const timerRef = useRef(null);

  const refresh = useCallback(() => {
    if (!uno) return;
    setLoading(true);
    setSilent(false);
    awaitingRef.current = true;
    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!awaitingRef.current) return;
      awaitingRef.current = false;
      setLoading(false);
      setSilent(true);
    }, LIST_TIMEOUT_MS);
  }, [uno, userId]);

  useEffect(() => {
    if (!uno || !enabled) return undefined;
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic || !awaitingRef.current) return;
      const nets = parseWifiList(payload);
      if (!nets.length && !SAYS_EMPTY(payload)) return;
      awaitingRef.current = false;
      clearTimeout(timerRef.current);
      setList(nets);
      setLoading(false);
    });
    refresh();
    return () => {
      off();
      unsubscribeTopic(topic);
      clearTimeout(timerRef.current);
      awaitingRef.current = false;
      setList(null);
    };
  }, [uno, enabled, refresh]);

  return { list, loading, silent, refresh };
}
